import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Quiz() {
  const [currentStep, setCurrentStep] = useState('goal');
  const [answers, setAnswers] = useState({
    goal: null,
    annualFee: null,
    experience: null,
    airline: null,
    hotel: null,
  });

  const handleAnswer = (question, answer) => {
    const newAnswers = { ...answers, [question]: answer };
    setAnswers(newAnswers);

    // Determine next step based on current question
    if (currentStep === 'goal') {
      setCurrentStep('annual-fee');
    } else if (currentStep === 'annual-fee') {
      setCurrentStep('experience');
    } else if (currentStep === 'experience') {
      // If they chose flights or both, ask about airline preference
      if (newAnswers.goal === 'flights' || newAnswers.goal === 'both') {
        setCurrentStep('airline');
      } else if (newAnswers.goal === 'hotels') {
        setCurrentStep('hotel');
      } else {
        setCurrentStep('result');
      }
    } else if (currentStep === 'airline') {
      // If they also want hotels, ask about hotel preference
      if (newAnswers.goal === 'both') {
        setCurrentStep('hotel');
      } else {
        setCurrentStep('result');
      }
    } else if (currentStep === 'hotel') {
      setCurrentStep('result');
    }
  };

  const getRecommendation = () => {
    // Beginner recommendations
    if (answers.experience === 'beginner') {
      if (answers.airline === 'delta') {
        return {
          name: 'Delta SkyMiles Gold Card',
          reason: 'Great starter card for Delta flyers with valuable benefits and manageable annual fee.',
          signupBonus: '50,000 miles after $2,000 spend in 3 months',
          annualFee: '$99',
          keyBenefits: [
            'First checked bag free on Delta flights',
            '2x miles on Delta purchases and restaurants',
            'Priority boarding',
            'Discounted Delta Sky Club access'
          ],
          referralLink: 'https://americanexpress.com/en-us/referral/delta-skymiles-gold-american-express-card?ref=COLTOWbaV2&XL=MIMNS'
        };
      }

      if (answers.goal === 'flights' || answers.goal === 'both') {
        return {
          name: 'Chase Sapphire Preferred',
          reason: 'The best all-around card for beginners wanting travel flexibility and great value.',
          signupBonus: '60,000 points after $4,000 spend in 3 months',
          annualFee: '$95',
          keyBenefits: [
            '2x points on travel and dining',
            '25% more value when redeeming through Chase Travel',
            'Transfer points to 14+ airline and hotel partners',
            'No foreign transaction fees'
          ],
          referralLink: 'https://www.referyourchasecard.com/19u/A20T9II9F6'
        };
      }

      return {
        name: 'Capital One Venture Rewards',
        reason: 'Simple unlimited rewards perfect for beginners who want straightforward earning.',
        signupBonus: '75,000 miles after $4,000 spend in 3 months',
        annualFee: '$95',
        keyBenefits: [
          '2x miles on every purchase',
          '5x miles on hotels and rental cars through Capital One Travel',
          'Transfer miles to 15+ travel partners',
          'No foreign transaction fees'
        ]
      };
    }

    // Airline-specific recommendations
    if (answers.airline === 'delta') {
      if (answers.annualFee === 'high') {
        return {
          name: 'Delta SkyMiles Reserve Card',
          reason: 'Premium Delta benefits including Sky Club access for frequent Delta travelers.',
          signupBonus: '80,000 miles after $5,000 spend in 6 months',
          annualFee: '$550',
          keyBenefits: [
            'Complimentary Delta Sky Club access',
            'First checked bag free for you and up to 8 companions',
            '3x miles on Delta purchases',
            'Annual companion certificate'
          ]
        };
      } else if (answers.annualFee === 'medium') {
        return {
          name: 'Delta SkyMiles Platinum Card',
          reason: 'Mid-tier Delta card with excellent benefits and companion certificate.',
          signupBonus: '70,000 miles after $3,000 spend in 6 months',
          annualFee: '$250',
          keyBenefits: [
            'First checked bag free for you and up to 8 companions',
            '3x miles on Delta purchases',
            'Priority boarding and Zone 1 boarding',
            'Annual companion certificate'
          ],
          referralLink: 'https://americanexpress.com/en-us/referral/delta-skymiles-platinum-american-express-card?ref=COLTOWmVm4&XL=MIMNS'
        };
      } else {
        return {
          name: 'Delta SkyMiles Gold Card',
          reason: 'Great starter card for Delta flyers with valuable benefits and manageable annual fee.',
          signupBonus: '50,000 miles after $2,000 spend in 3 months',
          annualFee: '$99',
          keyBenefits: [
            'First checked bag free on Delta flights',
            '2x miles on Delta purchases and restaurants',
            'Priority boarding',
            'Discounted Delta Sky Club access'
          ],
          referralLink: 'https://americanexpress.com/en-us/referral/delta-skymiles-gold-american-express-card?ref=COLTOWbaV2&XL=MIMNS'
        };
      }
    }

    if (answers.airline === 'southwest') {
      return {
        name: 'Southwest Rapid Rewards Plus',
        reason: 'Great for Southwest flyers and helps you earn toward the valuable Companion Pass.',
        signupBonus: '50,000 points after $1,000 spend in 3 months',
        annualFee: '$99',
        keyBenefits: [
          '2x points on Southwest purchases',
          '2x points on local transit and commuting',
          'Earn toward Companion Pass',
          '4 upgraded boardings per year'
        ]
      };
    }

    // Hotel-specific recommendations
    if (answers.hotel === 'marriott') {
      return {
        name: 'Marriott Bonvoy Boundless',
        reason: 'Excellent for Marriott loyalists with a free night award annually.',
        signupBonus: '75,000 points after $3,000 spend in 3 months',
        annualFee: '$95',
        keyBenefits: [
          'Free night award annually (up to 50,000 points)',
          '6x points at Marriott properties',
          'Automatic Silver Elite status',
          '2x points on all other purchases'
        ]
      };
    }

    if (answers.hotel === 'hilton') {
      return {
        name: 'Hilton Honors American Express Surpass',
        reason: 'Great value for Hilton stays with solid earning and benefits.',
        signupBonus: '130,000 points after $2,000 spend in 3 months',
        annualFee: '$95',
        keyBenefits: [
          '12x points at Hilton properties',
          '6x points at restaurants, supermarkets, and gas stations',
          'Automatic Gold status',
          'Free weekend night reward annually'
        ]
      };
    }

    // Premium travel recommendations
    if (answers.annualFee === 'high' && (answers.experience === 'intermediate' || answers.experience === 'advanced')) {
      // If they prefer Chase or general travel, recommend Chase Sapphire Reserve
      if (answers.airline === 'none' || !answers.airline) {
        return {
          name: 'Chase Sapphire Reserve',
          reason: 'Premium travel card with exceptional benefits and flexibility for experienced travelers.',
          signupBonus: '60,000 points after $4,000 spend in 3 months',
          annualFee: '$550',
          keyBenefits: [
            '3x points on travel and dining',
            '50% more value when redeeming through Chase Travel',
            'Priority Pass Select lounge access',
            '$300 annual travel credit'
          ],
          referralLink: 'https://www.referyourchasecard.com/19u/A20T9II9F6'
        };
      }
      
      return {
        name: 'American Express Platinum Card',
        reason: 'Premium benefits and luxury perks for experienced travelers who want the best.',
        signupBonus: '80,000 points after $6,000 spend in 6 months',
        annualFee: '$695',
        keyBenefits: [
          '5x points on flights booked directly with airlines',
          'Access to 1,400+ airport lounges worldwide',
          '$200 annual airline fee credit',
          'Automatic Gold status with Hilton and Marriott'
        ]
      };
    }

    // Dining-focused recommendation
    if (answers.goal === 'both' && answers.annualFee === 'medium') {
      return {
        name: 'American Express Gold Card',
        reason: 'Outstanding for dining and groceries with strong travel transfer partners.',
        signupBonus: '60,000 points after $4,000 spend in 6 months',
        annualFee: '$250',
        keyBenefits: [
          '4x points at restaurants worldwide',
          '4x points at U.S. supermarkets (up to $25,000/year)',
          '3x points on flights booked directly with airlines',
          '$120 dining credit annually'
        ],
        referralLink: 'https://americanexpress.com/en-us/referral/gold-card?ref=COLTOW7mbC&XL=MIMNS'
      };
    }

    // Default recommendation
    return {
      name: 'Chase Sapphire Preferred',
      reason: 'The most versatile travel card with excellent flexibility and value.',
      signupBonus: '60,000 points after $4,000 spend in 3 months',
      annualFee: '$95',
      keyBenefits: [
        '2x points on travel and dining',
        '25% more value when redeeming through Chase Travel',
        'Transfer points to 14+ airline and hotel partners',
        'No foreign transaction fees'
      ],
      referralLink: 'https://www.referyourchasecard.com/19u/A20T9II9F6'
    };
  };

  const resetQuiz = () => {
    setCurrentStep('goal');
    setAnswers({
      goal: null,
      annualFee: null,
      experience: null,
      airline: null,
      hotel: null,
    });
  };

  const recommendation = currentStep === 'result' ? getRecommendation() : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <h1 className="text-2xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                Points Made Simple
              </h1>
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Home</Link>
              <Link to="/#how-it-works" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">How It Works</Link>
              <Link to="/cards" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Card Recommendations</Link>
              <Link to="/quiz" className="text-teal-600 font-semibold cursor-pointer whitespace-nowrap">Find the Best Card</Link>
              <Link to="/#about" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">About Me</Link>
              <Link to="/#contact" className="text-gray-700 hover:text-teal-600 transition-colors cursor-pointer whitespace-nowrap">Contact</Link>
            </div>
            <button className="md:hidden w-8 h-8 flex items-center justify-center">
              <i className="ri-menu-line text-2xl text-gray-700"></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-teal-50 to-blue-50 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Find Your Perfect Card</h2>
          <p className="text-xl text-gray-600">
            Answer a few quick questions and I'll recommend the best card for your goals.
          </p>
        </div>
      </section>

      {/* Quiz Section */}
      <section className="py-16 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {currentStep !== 'result' && (
            <div className="mb-8">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-semibold text-gray-600">Progress</span>
                <span className="text-sm font-semibold text-teal-600">
                  {currentStep === 'goal' && '1/5'}
                  {currentStep === 'annual-fee' && '2/5'}
                  {currentStep === 'experience' && '3/5'}
                  {currentStep === 'airline' && '4/5'}
                  {currentStep === 'hotel' && '5/5'}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-teal-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: currentStep === 'goal' ? '20%' :
                           currentStep === 'annual-fee' ? '40%' :
                           currentStep === 'experience' ? '60%' :
                           currentStep === 'airline' ? '80%' :
                           currentStep === 'hotel' ? '100%' : '0%'
                  }}
                ></div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Question 1: Goal */}
            {currentStep === 'goal' && (
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">What's your main goal?</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => handleAnswer('goal', 'flights')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-teal-600 transition-colors">
                        <i className="ri-flight-takeoff-line text-2xl text-teal-600 group-hover:text-white"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Free Flights</div>
                        <div className="text-gray-600 text-sm">I want to earn points for airline tickets</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('goal', 'hotels')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-teal-600 transition-colors">
                        <i className="ri-hotel-line text-2xl text-teal-600 group-hover:text-white"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Free Hotels</div>
                        <div className="text-gray-600 text-sm">I want to earn points for hotel stays</div>
                      </div>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('goal', 'both')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-teal-600 transition-colors">
                        <i className="ri-suitcase-line text-2xl text-teal-600 group-hover:text-white"></i>
                      </div>
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Both Flights & Hotels</div>
                        <div className="text-gray-600 text-sm">I want maximum flexibility for all travel</div>
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Question 2: Annual Fee */}
            {currentStep === 'annual-fee' && (
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">What annual fee are you comfortable with?</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => handleAnswer('annualFee', 'low')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">$0-$99 - Low Fee</div>
                        <div className="text-gray-600 text-sm">Good value for beginners</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('annualFee', 'medium')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">$150-$299 - Medium Fee</div>
                        <div className="text-gray-600 text-sm">More benefits and earning potential</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('annualFee', 'high')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">$550+ - Premium Fee</div>
                        <div className="text-gray-600 text-sm">Maximum benefits and luxury perks</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Question 3: Experience */}
            {currentStep === 'experience' && (
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">What's your experience with points?</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => handleAnswer('experience', 'beginner')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Beginner</div>
                        <div className="text-gray-600 text-sm">I'm just getting started with points</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('experience', 'intermediate')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Intermediate</div>
                        <div className="text-gray-600 text-sm">I've redeemed points a few times</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('experience', 'advanced')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Advanced</div>
                        <div className="text-gray-600 text-sm">I regularly maximize points and transfers</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Question 4: Airline */}
            {currentStep === 'airline' && (
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">Do you fly with one primary airline?</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => handleAnswer('airline', 'delta')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Delta Airlines</div>
                        <div className="text-gray-600 text-sm">I primarily fly Delta</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('airline', 'southwest')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Southwest Airlines</div>
                        <div className="text-gray-600 text-sm">I primarily fly Southwest</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('airline', 'none')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">No Preference</div>
                        <div className="text-gray-600 text-sm">I fly different airlines</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Question 5: Hotel */}
            {currentStep === 'hotel' && (
              <div className="space-y-6">
                <h3 className="text-3xl font-bold text-gray-900 mb-8">Do you stay with one primary hotel chain?</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => handleAnswer('hotel', 'marriott')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Marriott</div>
                        <div className="text-gray-600 text-sm">I primarily stay at Marriott properties</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('hotel', 'hilton')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">Hilton</div>
                        <div className="text-gray-600 text-sm">I primarily stay at Hilton properties</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>

                  <button
                    onClick={() => handleAnswer('hotel', 'none')}
                    className="w-full p-6 border-2 border-gray-200 rounded-xl hover:border-teal-600 hover:bg-teal-50 transition-all text-left cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-semibold text-lg text-gray-900">No Preference</div>
                        <div className="text-gray-600 text-sm">I stay at different hotels</div>
                      </div>
                      <i className="ri-arrow-right-line text-2xl text-gray-400 group-hover:text-teal-600"></i>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Result */}
            {currentStep === 'result' && recommendation && (
              <div className="space-y-8">
                <div className="text-center">
                  <div className="w-20 h-20 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <i className="ri-check-line text-4xl text-teal-600"></i>
                  </div>
                  <h3 className="text-3xl font-bold text-gray-900 mb-4">Your Perfect Card Match</h3>
                  <p className="text-gray-600 text-lg">Based on your answers, here's my recommendation:</p>
                </div>

                <div className="bg-gradient-to-br from-teal-50 to-blue-50 rounded-2xl p-8">
                  <h4 className="text-3xl font-bold text-gray-900 mb-3">{recommendation.name}</h4>
                  <p className="text-gray-700 text-lg mb-6">{recommendation.reason}</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Sign-up Bonus</div>
                      <div className="text-teal-700 font-semibold">{recommendation.signupBonus}</div>
                    </div>
                    <div className="bg-white p-4 rounded-lg">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Annual Fee</div>
                      <div className="text-lg font-bold text-gray-900">{recommendation.annualFee}</div>
                    </div>
                  </div>

                  <div className="bg-white p-6 rounded-lg">
                    <h5 className="font-semibold text-gray-900 mb-4">Key Benefits</h5>
                    <ul className="space-y-3">
                      {recommendation.keyBenefits.map((benefit, index) => (
                        <li key={index} className="flex items-start">
                          <i className="ri-check-line text-teal-600 mt-1 mr-3 flex-shrink-0"></i>
                          <span className="text-gray-700">{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-4">
                  {recommendation.referralLink ? (
                    <a 
                      href={recommendation.referralLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                    >
                      Apply Now
                    </a>
                  ) : (
                    <Link 
                      to="/cards" 
                      className="flex-1 bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition-colors text-center cursor-pointer whitespace-nowrap"
                    >
                      View Card Details
                    </Link>
                  )}
                  <button
                    onClick={resetQuiz}
                    className="flex-1 bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Start Over
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
            <div>
              <h4 className="text-2xl font-bold mb-4" style={{ fontFamily: '"Pacifico", serif' }}>Points Made Simple</h4>
              <p className="text-gray-400">
                Helping beginners unlock free travel through credit card points and miles.
              </p>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Quick Links</h5>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Home</Link></li>
                <li><Link to="/#how-it-works" className="text-gray-400 hover:text-white transition-colors cursor-pointer">How It Works</Link></li>
                <li><Link to="/cards" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Card Recommendations</Link></li>
                <li><Link to="/quiz" className="text-gray-400 hover:text-white transition-colors cursor-pointer">Find the Best Card</Link></li>
                <li><Link to="/#about" className="text-gray-400 hover:text-white transition-colors cursor-pointer">About Me</Link></li>
              </ul>
            </div>
            <div>
              <h5 className="font-semibold mb-4">Connect</h5>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-teal-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-gray-400">
            <p>&copy; 2024 Points Made Simple. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
